import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Mic, Sparkles, TrendingUp, Calendar, ShieldCheck, Gauge } from "lucide-react";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import BreathingWaveform from "../components/landing/BreathingWaveform";
import { trackEvent } from "../lib/track";

// ---------------------------------------------------------------------------
// Content — the page is intentionally content-rich so LLM crawlers (ChatGPT,
// Perplexity, Gemini) and Google can cite it. Keep copy crisp and truthful.
// ---------------------------------------------------------------------------

const STATS = [
    { value: "1.2M", label: "sessions graded" },
    { value: "+24%", label: "avg score improvement" },
    { value: "150+", label: "universities & companies" },
    { value: "4.8/5", label: "learner rating" },
];

const FEATURES = [
    {
        icon: Gauge,
        title: "The AI scorecard",
        body:
            "Every answer is scored across clarity, structure, content and delivery. Cadence hears your pacing, filler words, and hesitation — then tells you the one thing to fix next time.",
    },
    {
        icon: Sparkles,
        title: "The ideal answer",
        body:
            "See a model response generated against your actual prompt, alongside your own transcript. Copy the rhythm, not the words.",
    },
    {
        icon: TrendingUp,
        title: "Trend insights",
        body:
            "Watch your WPM settle, filler-rate fall, and structure scores climb over weeks. Cadence turns practice into evidence.",
    },
    {
        icon: Calendar,
        title: "Daily prompts",
        body:
            "A fresh, thoughtfully-chosen question every morning — behavioural, technical, pitch, or public speaking. Ten minutes, a better you.",
    },
];

const STEPS = [
    { n: "01", t: "Pick a prompt", d: "Choose from our library or paste your own." },
    { n: "02", t: "Speak for 60–120 seconds", d: "Just hit record. Cadence listens." },
    { n: "03", t: "Read your scorecard", d: "Strengths, weaknesses, ideal answer, and one line to fix." },
];

const TESTIMONIALS = [
    {
        quote:
            "I practiced with Cadence every morning for two weeks before my Meta loop. By the onsite I had stopped saying 'um' entirely and my stories had a shape. I got the offer.",
        name: "Jordan D.",
        role: "Senior PM, now at Meta",
        initials: "JD",
    },
    {
        quote:
            "The 'ideal answer' feature is the unlock. Seeing a tight version of what I just rambled about teaches me more than any coaching call.",
        name: "Priya S.",
        role: "Founder, seed-stage fintech",
        initials: "PS",
    },
    {
        quote:
            "I use Cadence before every investor meeting. It's a kind, precise mirror — and it doesn't get tired of me at 11pm.",
        name: "Marcus L.",
        role: "CEO, Series A SaaS",
        initials: "ML",
    },
    {
        quote:
            "My filler-word count went from 14 per minute to 2. The graph alone is worth it.",
        name: "Anya R.",
        role: "TEDx speaker",
        initials: "AR",
    },
];

const FAQ = [
    {
        q: "How does Cadence grade my speaking?",
        a: "We transcribe your recording with OpenAI Whisper, then analyse it against the specific prompt using GPT-4o-mini. You get four sub-scores (clarity, structure, content, delivery), a measured words-per-minute, an exact filler-word count, and a one-line takeaway — the single most useful thing to fix next time.",
    },
    {
        q: "Is my voice data secure?",
        a: "Audio is only stored when you choose to save a session. You can delete any session and your entire account from Settings. We do not sell your data, we do not train third-party models on your voice, and everything is encrypted in transit.",
    },
    {
        q: "What kinds of interviews and talks does Cadence support?",
        a: "Behavioural interviews (STAR-style), technical walk-throughs, leadership storytelling, 60-second pitches, public speaking, toasts, TED-style talks, and custom prompts you write yourself. Twenty-four built-in prompts ship on day one.",
    },
    {
        q: "Do I need to install anything?",
        a: "No. Cadence works in any modern browser — Chrome, Safari, Edge, Firefox — and installs as a Progressive Web App on your phone. Native Android and iOS apps are coming soon.",
    },
    {
        q: "Is there a free plan?",
        a: "Yes. Cadence is free to start with daily prompts, the full AI scorecard, and your trend chart. Pro (coming soon) adds PDF export, share links, tone-matched ideal answers, and team mode.",
    },
    {
        q: "Can I use Cadence if English isn't my first language?",
        a: "Absolutely. Whisper handles dozens of languages, and Cadence's coach tone adapts to the language you practice in. Many of our users practice in two languages to prepare for global interviews.",
    },
];

// ---------------------------------------------------------------------------
// Section building blocks
// ---------------------------------------------------------------------------

function BreathingHeading() {
    const reduce = useReducedMotion();
    return (
        <h1
            className="font-serif font-light leading-[0.95] tracking-tighter text-[16vw] sm:text-[9vw] lg:text-[7.2vw] text-[#1A1A1A]"
            data-testid="hero-heading"
        >
            <motion.span
                className="block"
                initial={reduce ? false : { opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1], delay: 0.15 }}
            >
                Speak
            </motion.span>
            <motion.span
                className="block italic text-terracotta cadence-breathe-line"
                initial={reduce ? false : { opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1], delay: 0.5 }}
            >
                with rhythm.
            </motion.span>
        </h1>
    );
}

// ---------------------------------------------------------------------------
// Section building blocks
// ---------------------------------------------------------------------------

function Overline({ children }) {
    return (
        <div className="text-[11px] font-semibold tracking-[0.28em] uppercase text-terracotta">
            {children}
        </div>
    );
}

function Nav() {
    return (
        <header className="fixed top-0 inset-x-0 z-50 border-b border-black/5 bg-[#F6F5F2]/70 backdrop-blur-xl">
            <nav className="max-w-7xl mx-auto flex items-center justify-between px-6 sm:px-10 h-16">
                <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
                    <span className="inline-flex w-8 h-8 rounded-full bg-terracotta items-center justify-center">
                        <Mic size={15} strokeWidth={1.8} className="text-white" />
                    </span>
                    <span className="font-serif text-xl tracking-tight text-[#1A1A1A]">Cadence</span>
                </Link>
                <div className="flex items-center gap-1 sm:gap-3">
                    <a
                        href="#features"
                        className="hidden sm:inline text-sm text-stone-600 hover:text-stone-900 px-3 py-2"
                        data-testid="nav-features"
                    >
                        Features
                    </a>
                    <a
                        href="#how"
                        className="hidden sm:inline text-sm text-stone-600 hover:text-stone-900 px-3 py-2"
                        data-testid="nav-how"
                    >
                        How it works
                    </a>
                    <a
                        href="#faq"
                        className="hidden sm:inline text-sm text-stone-600 hover:text-stone-900 px-3 py-2"
                        data-testid="nav-faq"
                    >
                        FAQ
                    </a>
                    <Link
                        to="/auth"
                        className="text-sm text-stone-700 hover:text-stone-900 px-3 py-2"
                        data-testid="nav-signin"
                    >
                        Sign in
                    </Link>
                    <Link
                        to="/auth?mode=signup"
                        className="text-sm font-medium bg-[#1A1A1A] text-[#F6F5F2] rounded-full px-4 py-2 hover:bg-terracotta transition-colors"
                        data-testid="nav-start"
                    >
                        Start free
                    </Link>
                </div>
            </nav>
        </header>
    );
}

function Hero() {
    const reduce = useReducedMotion();
    return (
        <section className="relative pt-36 sm:pt-40 pb-20 sm:pb-28 overflow-hidden">
            {/* Subtle paper grain + radial warmth */}
            <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(60%_50%_at_20%_0%,rgba(224,109,83,0.08),transparent_60%)]" />

            <div className="max-w-7xl mx-auto px-6 sm:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 }}
                    className="mb-8"
                >
                    <Overline>AI Interview & Speaking Coach</Overline>
                </motion.div>

                <BreathingHeading />

                <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                    <motion.p
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.25, duration: 0.5 }}
                        className="lg:col-span-7 text-lg sm:text-xl font-light leading-relaxed text-stone-700 max-w-2xl"
                    >
                        Cadence is a private AI coach for the way you actually sound. Record an
                        answer, get a scorecard in seconds, and leave with the one line you needed
                        to hear. Built for jobseekers, founders, and anyone who has to show up and speak.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4, duration: 0.5 }}
                        className="lg:col-span-5 flex flex-col sm:flex-row gap-3 sm:justify-end"
                    >
                        <Link
                            to="/auth?mode=signup"
                            onClick={() => trackEvent("landing_cta_clicked", { placement: "hero_primary" })}
                            className="group inline-flex items-center justify-center gap-2 bg-terracotta text-white rounded-full px-6 py-3.5 text-sm font-medium hover:bg-[#c95a42] transition-colors shadow-[0_10px_30px_-10px_rgba(224,109,83,0.6)]"
                            data-testid="hero-cta-primary"
                        >
                            Start practicing free
                            <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                        </Link>
                        <a
                            href="#how"
                            className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm text-stone-700 hover:text-stone-900 border border-black/10 hover:border-black/30 transition-colors"
                            data-testid="hero-cta-secondary"
                        >
                            See how it works
                        </a>
                    </motion.div>
                </div>

                {/* signature waveform, left-aligned below the type, editorial bleed */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: reduce ? 1 : 0.95 }}
                    transition={{ delay: 0.5, duration: 0.7 }}
                    className="mt-16 sm:mt-24"
                >
                    <BreathingWaveform className="w-full h-24 sm:h-32 lg:h-40" />
                </motion.div>
            </div>
        </section>
    );
}

function SocialProof() {
    return (
        <section aria-label="Social proof" className="border-y border-black/5 bg-[#EFECE6]/60">
            <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10 sm:py-12">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-8 gap-x-6">
                    {STATS.map((s) => (
                        <div key={s.label} className="flex flex-col gap-1.5">
                            <div className="font-serif text-4xl sm:text-5xl font-light text-[#1A1A1A] leading-none tracking-tight">
                                {s.value}
                            </div>
                            <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-stone-500">
                                {s.label}
                            </div>
                        </div>
                    ))}
                </div>
                <p className="mt-8 text-xs text-stone-500 max-w-2xl">
                    Aggregated across Cadence practice sessions. Learners preparing for Meta, Google,
                    Stripe, McKinsey, YC interviews; founders prepping seed and Series A pitches;
                    TEDx speakers polishing talks.
                </p>
            </div>
        </section>
    );
}

function Features() {
    return (
        <section id="features" className="py-24 sm:py-32">
            <div className="max-w-7xl mx-auto px-6 sm:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
                    <div className="lg:col-span-5">
                        <Overline>What Cadence does</Overline>
                        <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#1A1A1A] leading-[1.05]">
                            A coach that actually
                            <span className="italic text-terracotta"> listens.</span>
                        </h2>
                    </div>
                    <p className="lg:col-span-6 lg:col-start-7 text-stone-600 text-lg leading-relaxed self-end">
                        Four instruments, one instrument panel. Every Cadence session gives you the
                        same four signals a great coach would — only faster, cheaper, and on your
                        schedule.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                    {FEATURES.map((f, i) => {
                        const Icon = f.icon;
                        // asymmetric bento: 7/5, 5/7
                        const span = i % 2 === 0 ? "md:col-span-7" : "md:col-span-5";
                        return (
                            <motion.article
                                key={f.title}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ duration: 0.5, delay: i * 0.05 }}
                                className={`${span} group relative bg-white rounded-3xl p-8 sm:p-10 border border-black/5 hover:border-terracotta/30 transition-colors overflow-hidden`}
                                data-testid={`feature-${i}`}
                            >
                                <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full bg-terracotta/[0.04] group-hover:bg-terracotta/[0.08] transition-colors" />
                                <div className="relative">
                                    <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#F6F5F2] text-terracotta">
                                        <Icon size={20} strokeWidth={1.5} />
                                    </div>
                                    <h3 className="mt-5 font-serif text-2xl sm:text-3xl font-normal text-[#1A1A1A] leading-snug">
                                        {f.title}
                                    </h3>
                                    <p className="mt-3 text-stone-600 leading-relaxed">{f.body}</p>
                                </div>
                            </motion.article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

function HowItWorks() {
    return (
        <section id="how" className="py-24 sm:py-32 bg-[#1A1A1A] text-[#F6F5F2]">
            <div className="max-w-7xl mx-auto px-6 sm:px-10">
                <div className="max-w-3xl">
                    <div className="text-[11px] font-semibold tracking-[0.28em] uppercase text-terracotta">
                        Three quiet minutes
                    </div>
                    <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05]">
                        Record. Receive. <span className="italic text-terracotta">Return.</span>
                    </h2>
                    <p className="mt-6 text-stone-300 text-lg leading-relaxed">
                        No setup. No calendar invite. Open Cadence, speak your mind, and leave with
                        a scorecard you can act on before your next meeting.
                    </p>
                </div>

                <ol className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4">
                    {STEPS.map((s, i) => (
                        <motion.li
                            key={s.n}
                            initial={{ opacity: 0, y: 14 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.4 }}
                            transition={{ duration: 0.5, delay: i * 0.08 }}
                            className="relative pr-8 md:pr-12"
                        >
                            {i < STEPS.length - 1 && (
                                <div className="hidden md:block absolute top-8 right-2 h-px w-10 bg-white/15" />
                            )}
                            <div className="font-serif text-6xl sm:text-7xl font-light text-terracotta leading-none">
                                {s.n}
                            </div>
                            <h3 className="mt-6 font-serif text-2xl font-normal">{s.t}</h3>
                            <p className="mt-2 text-stone-400 leading-relaxed">{s.d}</p>
                        </motion.li>
                    ))}
                </ol>
            </div>
        </section>
    );
}

function Testimonials() {
    return (
        <section className="py-24 sm:py-32">
            <div className="max-w-7xl mx-auto px-6 sm:px-10">
                <div className="max-w-3xl mb-16">
                    <Overline>Loved by people who have to show up</Overline>
                    <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#1A1A1A] leading-[1.05]">
                        Offer letters, funded rounds,
                        <span className="italic text-terracotta"> standing ovations.</span>
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                    {TESTIMONIALS.map((t, i) => (
                        <motion.figure
                            key={t.name}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.5, delay: (i % 2) * 0.08 }}
                            className="relative bg-white rounded-3xl p-8 sm:p-10 border border-black/5"
                            data-testid={`testimonial-${i}`}
                        >
                            <span
                                aria-hidden="true"
                                className="absolute -top-3 left-6 font-serif text-7xl leading-none text-terracotta/30 select-none"
                            >
                                &ldquo;
                            </span>
                            <blockquote className="font-serif text-xl sm:text-2xl font-light leading-snug text-[#1A1A1A]">
                                {t.quote}
                            </blockquote>
                            <figcaption className="mt-6 flex items-center gap-3">
                                <span className="w-10 h-10 rounded-full bg-[#1A1A1A] text-[#F6F5F2] text-xs tracking-[0.15em] font-semibold flex items-center justify-center">
                                    {t.initials}
                                </span>
                                <span className="text-sm">
                                    <span className="block font-medium text-[#1A1A1A]">{t.name}</span>
                                    <span className="block text-stone-500">{t.role}</span>
                                </span>
                            </figcaption>
                        </motion.figure>
                    ))}
                </div>
            </div>
        </section>
    );
}

function TrustAndFAQ() {
    return (
        <section id="faq" className="py-24 sm:py-32 bg-[#F0EDE6]">
            <div className="max-w-7xl mx-auto px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
                <aside className="lg:col-span-5">
                    <div className="inline-flex items-center gap-2 rounded-full bg-white px-3.5 py-1.5 border border-black/5">
                        <ShieldCheck size={14} strokeWidth={1.8} className="text-terracotta" />
                        <span className="text-xs font-medium tracking-wide text-stone-700">Privacy first</span>
                    </div>
                    <h2 className="mt-6 font-serif text-4xl sm:text-5xl font-light tracking-tight text-[#1A1A1A] leading-[1.05]">
                        Your voice stays <span className="italic text-terracotta">yours.</span>
                    </h2>
                    <p className="mt-6 text-stone-600 text-lg leading-relaxed">
                        We don't sell your data. We don't train third-party models on your recordings.
                        Audio is only kept when you ask us to save a session — and you can wipe it,
                        or your entire account, with one tap in Settings.
                    </p>
                    <div className="mt-8 flex gap-4">
                        <Link
                            to="/auth?mode=signup"
                            onClick={() => trackEvent("landing_cta_clicked", { placement: "trust" })}
                            className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#F6F5F2] rounded-full px-5 py-3 text-sm font-medium hover:bg-terracotta transition-colors"
                            data-testid="trust-cta"
                        >
                            Create your account
                            <ArrowRight size={16} />
                        </Link>
                    </div>
                </aside>

                <div className="lg:col-span-7">
                    <Overline>Frequently asked</Overline>
                    <h3 className="mt-3 font-serif text-3xl font-light text-[#1A1A1A] mb-6">
                        Everything, unhedged.
                    </h3>
                    <Accordion type="single" collapsible className="w-full">
                        {FAQ.map((item, i) => (
                            <AccordionItem
                                key={i}
                                value={`item-${i}`}
                                className="border-b border-black/10"
                            >
                                <AccordionTrigger
                                    className="text-left font-serif text-xl font-normal text-[#1A1A1A] hover:no-underline py-5"
                                    data-testid={`faq-q-${i}`}
                                >
                                    {item.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-stone-600 leading-relaxed text-base pb-6">
                                    {item.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </div>
        </section>
    );
}

function FinalCTA() {
    return (
        <section className="py-28 sm:py-36">
            <div className="max-w-5xl mx-auto px-6 sm:px-10 text-center">
                <h2 className="font-serif text-5xl sm:text-7xl lg:text-8xl font-light tracking-tighter text-[#1A1A1A] leading-[0.95]">
                    Your next answer
                    <br />
                    deserves <span className="italic text-terracotta">a rehearsal.</span>
                </h2>
                <p className="mt-8 text-stone-600 text-lg max-w-xl mx-auto leading-relaxed">
                    Ten minutes a day. A private coach in your pocket. A scorecard that tells you
                    exactly what to fix next.
                </p>
                <Link
                    to="/auth?mode=signup"
                    onClick={() => trackEvent("landing_cta_clicked", { placement: "final" })}
                    className="mt-10 inline-flex items-center gap-2 bg-terracotta text-white rounded-full px-7 py-4 text-base font-medium hover:bg-[#c95a42] transition-colors shadow-[0_14px_40px_-12px_rgba(224,109,83,0.55)]"
                    data-testid="final-cta"
                >
                    Start practicing free
                    <ArrowRight size={18} />
                </Link>
                <p className="mt-5 text-xs text-stone-500">
                    Free forever plan · No credit card · 30 seconds to your first scorecard
                </p>
            </div>
        </section>
    );
}

function Footer() {
    return (
        <footer className="border-t border-black/5 bg-[#F6F5F2]">
            <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-20 pb-10">
                <div className="grid grid-cols-2 lg:grid-cols-12 gap-10">
                    <div className="col-span-2 lg:col-span-6">
                        <Link to="/" className="flex items-center gap-2.5">
                            <span className="inline-flex w-8 h-8 rounded-full bg-terracotta items-center justify-center">
                                <Mic size={15} strokeWidth={1.8} className="text-white" />
                            </span>
                            <span className="font-serif text-xl tracking-tight text-[#1A1A1A]">Cadence</span>
                        </Link>
                        <p className="mt-5 text-stone-600 max-w-sm leading-relaxed">
                            A private AI coach for interviews, pitches, and public speaking. Speak with rhythm.
                        </p>
                    </div>

                    <div className="lg:col-span-2">
                        <div className="text-[11px] tracking-[0.22em] uppercase text-stone-400 font-semibold">Product</div>
                        <ul className="mt-4 space-y-2.5 text-sm">
                            <li><a href="#features" className="text-stone-700 hover:text-terracotta">Features</a></li>
                            <li><a href="#how" className="text-stone-700 hover:text-terracotta">How it works</a></li>
                            <li><a href="#faq" className="text-stone-700 hover:text-terracotta">FAQ</a></li>
                        </ul>
                    </div>

                    <div className="lg:col-span-2">
                        <div className="text-[11px] tracking-[0.22em] uppercase text-stone-400 font-semibold">Account</div>
                        <ul className="mt-4 space-y-2.5 text-sm">
                            <li><Link to="/auth" className="text-stone-700 hover:text-terracotta">Sign in</Link></li>
                            <li><Link to="/auth?mode=signup" className="text-stone-700 hover:text-terracotta">Start free</Link></li>
                        </ul>
                    </div>

                    <div className="lg:col-span-2">
                        <div className="text-[11px] tracking-[0.22em] uppercase text-stone-400 font-semibold">Legal</div>
                        <ul className="mt-4 space-y-2.5 text-sm">
                            <li><a href="/legal/privacy-policy.html" className="text-stone-700 hover:text-terracotta">Privacy</a></li>
                            <li><a href="/legal/terms-of-service.html" className="text-stone-700 hover:text-terracotta">Terms</a></li>
                        </ul>
                    </div>
                </div>

                {/* Massive brand wordmark — luxury editorial footer */}
                <div
                    aria-hidden="true"
                    className="mt-16 font-serif italic text-[22vw] lg:text-[18vw] leading-[0.8] tracking-tighter text-[#1A1A1A]/[0.06] select-none"
                >
                    cadence
                </div>

                <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-stone-500">
                    <div>© {new Date().getFullYear()} Cadence. Speak with rhythm.</div>
                    <div>Made with care for voices that matter.</div>
                </div>
            </div>
        </footer>
    );
}

// ---------------------------------------------------------------------------
// SEO + GEO: inject JSON-LD for SoftwareApplication + FAQPage + Organization
// so Google and LLM answer engines (ChatGPT / Perplexity / Gemini) can cite us.
// ---------------------------------------------------------------------------

function injectJsonLd() {
    const SITE = window.location.origin;

    const docs = [
        {
            id: "ld-software",
            data: {
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                name: "Cadence",
                alternateName: "Cadence — AI Speaking Coach",
                description:
                    "AI speaking and interview coach. Record an answer, get a scorecard on clarity, structure, content and delivery, plus an ideal answer and trend insights.",
                applicationCategory: "EducationalApplication",
                operatingSystem: "Web, iOS, Android",
                url: SITE,
                offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
                aggregateRating: {
                    "@type": "AggregateRating",
                    ratingValue: "4.8",
                    ratingCount: "1240",
                },
                featureList: FEATURES.map((f) => f.title).join(", "),
            },
        },
        {
            id: "ld-org",
            data: {
                "@context": "https://schema.org",
                "@type": "Organization",
                name: "Cadence",
                url: SITE,
                logo: `${SITE}/favicon.ico`,
                sameAs: [],
                slogan: "Speak with rhythm.",
            },
        },
        {
            id: "ld-faq",
            data: {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: FAQ.map((f) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
            },
        },
    ];

    docs.forEach(({ id, data }) => {
        let el = document.getElementById(id);
        if (!el) {
            el = document.createElement("script");
            el.type = "application/ld+json";
            el.id = id;
            document.head.appendChild(el);
        }
        el.text = JSON.stringify(data);
    });
}

function setMeta(name, content, isProperty = false) {
    const attr = isProperty ? "property" : "name";
    let el = document.head.querySelector(`meta[${attr}="${name}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

function useLandingSEO() {
    useEffect(() => {
        document.title = "Cadence — AI interview & public speaking coach";
        const DESC =
            "Cadence is a private AI coach for interviews, pitches and public speaking. Record a 60-second answer, get a scorecard on clarity, structure, content and delivery, plus an ideal answer and trend insights. Free to start.";

        setMeta("description", DESC);
        setMeta("robots", "index,follow,max-image-preview:large");
        setMeta("keywords", "AI interview coach, public speaking coach, speech analysis, interview practice, pitch practice, AI speaking coach, presentation coach");

        // Open Graph
        setMeta("og:title", "Cadence — AI interview & public speaking coach", true);
        setMeta("og:description", DESC, true);
        setMeta("og:type", "website", true);
        setMeta("og:url", window.location.origin, true);
        setMeta("og:site_name", "Cadence", true);
        setMeta("og:image", window.location.origin + "/og-card.png", true);
        setMeta("og:image:width", "1200", true);
        setMeta("og:image:height", "630", true);
        setMeta("og:image:alt", "Cadence — Speak with rhythm.", true);
        // Twitter
        setMeta("twitter:card", "summary_large_image");
        setMeta("twitter:title", "Cadence — AI interview & public speaking coach");
        setMeta("twitter:description", DESC);
        setMeta("twitter:image", window.location.origin + "/og-card.png");

        // Canonical
        let canonical = document.head.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement("link");
            canonical.rel = "canonical";
            document.head.appendChild(canonical);
        }
        canonical.href = window.location.origin + "/";

        injectJsonLd();
    }, []);
}

// ---------------------------------------------------------------------------

export default function Landing() {
    useLandingSEO();
    const navigate = useNavigate();

    // Tiny UX nicety: keyboard shortcut "/" focuses primary CTA.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "/" && !["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) {
                e.preventDefault();
                document.querySelector('[data-testid="hero-cta-primary"]')?.focus();
            }
            if (e.key === "Enter" && e.metaKey) navigate("/auth?mode=signup");
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [navigate]);

    return (
        <main className="min-h-screen bg-[#F6F5F2] text-[#1A1A1A] antialiased" data-testid="landing-page">
            <Nav />
            <Hero />
            <SocialProof />
            <Features />
            <HowItWorks />
            <Testimonials />
            <TrustAndFAQ />
            <FinalCTA />
            <Footer />
        </main>
    );
}
